import { z } from 'zod';
import {
    isApplicationNotificationTemplateCode,
    lintNotificationTemplateVariables,
} from './notification-template-catalog.js';
import {
    isCuratedNotificationEvent,
    NOTIFICATION_POLICY_SYSTEM_VARIABLES,
} from './notification-routing.registry.js';
import {
    lockedVariablesForBinding,
    NOTIFICATION_TEMPLATE_BINDING_KINDS,
} from './notification-variable-contract.js';

export const NOTIFICATION_TEMPLATE_CHANNELS = ['in-app', 'email', 'sms', 'push'];
export const NOTIFICATION_TEMPLATE_LOCALES = ['tr', 'en'];

export const NotificationTemplateBindingSchema = z.object({
    kind: z.enum(NOTIFICATION_TEMPLATE_BINDING_KINDS),
    eventName: z.string().trim().min(3).max(160).optional().nullable(),
}).strict().superRefine((binding, context) => {
    if (binding.kind === 'event') {
        if (!binding.eventName) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['eventName'],
                message: 'eventName is required when binding.kind is event',
            });
            return;
        }
        if (!isCuratedNotificationEvent(binding.eventName)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['eventName'],
                message: `Unknown curated notification event: ${binding.eventName}`,
            });
        }
        return;
    }
    if (binding.eventName) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['eventName'],
            message: 'eventName is only allowed when binding.kind is event',
        });
    }
});

const requiredText = (max) => z.string().trim().min(1).max(max);
const optionalText = (max) => z.string().max(max).optional().default('');
const enabledVariant = { enabled: z.boolean().default(true) };

export const NotificationTemplateNameSchema = z.record(z.string(), z.string().trim().max(120)).refine(
    (value) => Object.values(value).some((text) => text.length > 0),
    { message: 'At least one locale name is required' },
);

export const NotificationEmailVariantSchema = z.object({
    ...enabledVariant,
    subject: requiredText(300),
    html: requiredText(100_000),
    text: optionalText(50_000),
}).strict();

export const NotificationSmsVariantSchema = z.object({
    ...enabledVariant,
    text: requiredText(2_000),
}).strict();

export const NotificationPushVariantSchema = z.object({
    ...enabledVariant,
    title: requiredText(300),
    body: requiredText(4_000),
    actionUrl: optionalText(2_000),
}).strict();

export const NotificationInAppVariantSchema = z.object({
    ...enabledVariant,
    title: requiredText(300),
    body: requiredText(20_000),
    actionUrl: optionalText(2_000),
}).strict();

export const NotificationLocaleVariantsSchema = z.object({
    email: NotificationEmailVariantSchema.optional(),
    sms: NotificationSmsVariantSchema.optional(),
    push: NotificationPushVariantSchema.optional(),
    'in-app': NotificationInAppVariantSchema.optional(),
}).strict().refine((value) => Object.values(value).some(Boolean), {
    message: 'At least one channel variant is required',
});

export const NotificationTemplateVariantsSchema = z.record(
    z.enum(NOTIFICATION_TEMPLATE_LOCALES),
    NotificationLocaleVariantsSchema,
).refine((value) => Object.keys(value).length > 0, {
    message: 'At least one locale variant is required',
});

function validateDefaultLocaleVariant(value, context) {
    if (!value.defaultLocale || !value.variants) return;
    if (Object.prototype.hasOwnProperty.call(value.variants, value.defaultLocale)) return;
    context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['variants', value.defaultLocale],
        message: `Default locale '${value.defaultLocale}' must have a template variant`,
    });
}

const notificationTemplateShape = {
    name: NotificationTemplateNameSchema,
    code: z.string().trim().min(2).max(120).regex(/^[A-Z][A-Z0-9_]*$/),
    category: z.string().trim().max(120).optional().default(''),
    defaultLocale: z.enum(NOTIFICATION_TEMPLATE_LOCALES).default('en'),
    binding: NotificationTemplateBindingSchema.optional().default({ kind: 'workflow' }),
    variables: z.array(z.string().trim().min(1).max(120)).max(100).default([]),
    variants: NotificationTemplateVariantsSchema,
    status: z.enum(['active', 'inactive']).default('active'),
    customFields: z.record(z.unknown()).optional(),
};

function validateBindingAndVariables(value, context) {
    if (!value) return;
    const binding = value.binding || { kind: 'workflow' };
    if (binding.kind === 'application' && value.code && !isApplicationNotificationTemplateCode(value.code)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['binding', 'kind'],
            message: `Code ${value.code} is not an application notification template`,
        });
    }
    if (binding.kind === 'event' && value.code && isApplicationNotificationTemplateCode(value.code)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['binding', 'kind'],
            message: 'Application catalog codes must use binding.kind application',
        });
    }

    const locked = lockedVariablesForBinding(binding, value.code);
    if (locked && Array.isArray(value.variables)) {
        const declared = new Set(value.variables.map(String));
        const missing = locked.filter((key) => !declared.has(key));
        const extra = value.variables.map(String).filter((key) => !locked.includes(key));
        if (missing.length || extra.length) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['variables'],
                message: `Variables must match the ${binding.kind} contract`,
            });
        }
    }

    if (!value.variants || !Array.isArray(value.variables)) return;
    const systemKeys = binding.kind === 'event'
        ? NOTIFICATION_POLICY_SYSTEM_VARIABLES.map(({ key }) => key)
        : [];
    const { undeclared } = lintNotificationTemplateVariables({
        declared: [...value.variables, ...systemKeys],
        variants: value.variants,
    });
    if (undeclared.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['variables'],
            message: `Undeclared template variables: ${undeclared.join(', ')}`,
        });
    }
}

export const CreateNotificationTemplateSchema = z.object(notificationTemplateShape)
    .strict()
    .superRefine(validateDefaultLocaleVariant)
    .superRefine(validateBindingAndVariables);

export const UpdateNotificationTemplateSchema = z.object(
    Object.fromEntries(
        Object.entries(notificationTemplateShape).map(([key, schema]) => [key, schema.optional()]),
    ),
).strict().refine((value) => Object.keys(value).length > 0, {
    message: 'At least one notification template field must be updated',
}).superRefine(validateDefaultLocaleVariant)
    .superRefine(validateBindingAndVariables);

export const NotificationTemplateFormSchema = z.object(notificationTemplateShape)
    .strict()
    .superRefine(validateDefaultLocaleVariant)
    .superRefine(validateBindingAndVariables);

export const NotificationTemplateVariableContractQuerySchema = z.object({
    eventName: z.string().trim().min(3).max(160).optional(),
    code: z.string().trim().min(2).max(120).optional(),
    variables: z.string().trim().max(4_000).optional(),
}).strict().refine((value) => Boolean(value.eventName || value.code || value.variables != null), {
    message: 'eventName, code, or variables is required',
});
